import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UserCircleIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
  SparklesIcon,
  CheckIcon,
  ShareIcon,
  ClipboardDocumentIcon,
  GiftIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Loading } from '../components/common';
import { getSubscription, createPortalSession } from '../services/paymentService';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  // Form state
  const [name, setName] = useState(user?.name || '');

  // Referral data
  const referralCode = user?.referral?.code || '';
  const referralCount = user?.referral?.count || 0;
  const freeDaysAvailable = user?.referral?.freeDaysAvailable || 0;
  const freeDaysEarned = referralCount * 7;
  const referralLink = `https://kwikly.me/register?ref=${referralCode}`;

  useEffect(() => {
    fetchSubscription();

    // Show success message if redirected from Stripe
    if (searchParams.get('payment') === 'success') {
      toast.success('Welcome to Pro! Your subscription is now active.');
    }
  }, [searchParams]);

  const fetchSubscription = async () => {
    try {
      const response = await getSubscription();
      if (response.success) {
        setSubscription(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // This would call an API to update user profile
      // For now, just show success
      await updateUser({ name });
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const response = await createPortalSession();
      if (response.success && response.data.portalUrl) {
        window.location.href = response.data.portalUrl;
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to open billing portal');
    } finally {
      setManagingBilling(false);
    }
  };

  const handleCopyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setReferralCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setReferralCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareReferral = async () => {
    const shareData = {
      title: 'Join Kwikly - Beat ATS in 60 seconds',
      text: 'I use Kwikly to tailor my resume for each job in 60 seconds. Get 7 free days of Pro when you sign up!',
      url: referralLink
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyReferralLink();
        }
      }
    } else {
      handleCopyReferralLink();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" text="Loading settings..." />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-600">Manage your account and preferences</p>
      </div>

      {/* Profile Section */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <UserCircleIcon className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
        </div>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />

          <Input
            label="Email"
            value={user?.email || ''}
            disabled
            helperText="Email cannot be changed"
          />

          <div className="flex justify-end">
            <Button type="submit" loading={saving}>
              Save Changes
            </Button>
          </div>
        </form>
      </Card>

      {/* Subscription Section */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <CreditCardIcon className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {subscription?.plan === 'pro' ? (
                <>
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <SparklesIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Pro Plan</p>
                    <p className="text-sm text-gray-600">Unlimited access to all features</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-2 bg-gray-200 rounded-lg">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Free Plan</p>
                    <p className="text-sm text-gray-600">
                      {subscription?.limits?.jobCapturesPerWeek} captures/week, {subscription?.limits?.tailorsPerMonth} tailors/month
                    </p>
                  </div>
                </>
              )}
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              subscription?.plan === 'pro'
                ? 'bg-primary-100 text-primary-800'
                : 'bg-gray-200 text-gray-800'
            }`}>
              {subscription?.plan === 'pro' ? 'Active' : 'Free'}
            </span>
          </div>

          {/* Stripe subscription info */}
          {subscription?.subscription && subscription?.paymentGateway === 'stripe' && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
              <p>
                {subscription.subscription.cancelAtPeriodEnd
                  ? `Cancels on ${new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString()}`
                  : `Renews on ${new Date(subscription.subscription.currentPeriodEnd).toLocaleDateString()}`}
              </p>
            </div>
          )}

          {/* Razorpay one-time payment info */}
          {subscription?.plan === 'pro' && subscription?.paymentGateway === 'razorpay' && subscription?.proExpiresAt && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
              <p>
                Pro access expires on {new Date(subscription.proExpiresAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {subscription?.plan === 'pro' ? (
          subscription?.hasPortal ? (
            <Button variant="secondary" onClick={handleManageBilling} loading={managingBilling}>
              Manage Billing
            </Button>
          ) : (
            <div className="text-sm text-gray-600 bg-gray-100 rounded-lg p-3">
              <p className="font-medium text-gray-700 mb-1">Need help with billing?</p>
              <p>Contact us at <a href="mailto:support@kwikly.com" className="text-primary-600 hover:underline">support@kwikly.com</a> for any billing inquiries or to request a refund.</p>
            </div>
          )
        ) : (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-4">
              <h3 className="font-semibold text-primary-900 mb-2">Upgrade to Pro</h3>
              <ul className="space-y-2 mb-4">
                {[
                  'Unlimited job captures',
                  'Unlimited resume tailoring',
                  'Watermark-free exports',
                  'Priority support'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-primary-800">
                    <CheckIcon className="h-4 w-4 text-primary-600" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/pricing')}>
                <SparklesIcon className="h-5 w-5 mr-2" />
                Upgrade to Pro - $19/month
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Referral Section */}
      {referralCode && (
        <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <div className="flex items-center gap-3 mb-6">
            <GiftIcon className="h-6 w-6 text-teal-500" />
            <h2 className="text-lg font-semibold text-teal-900">Refer a Friend</h2>
          </div>

          <p className="text-sm text-teal-700 mb-4">
            Share Kwikly with friends and earn 7 free Pro days for each signup! Your friends also get 7 free days.
          </p>

          {/* Referral link */}
          <div className="bg-white rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600 mb-2">Your referral link:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-50 px-3 py-2 rounded border border-gray-200 text-sm text-gray-700 truncate">
                {referralLink}
              </code>
              <Button
                size="sm"
                variant={referralCopied ? 'secondary' : 'primary'}
                onClick={handleCopyReferralLink}
              >
                {referralCopied ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stats summary */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center bg-white rounded-lg p-3">
              <div className="flex items-center justify-center mb-1">
                <UserGroupIcon className="h-4 w-4 text-teal-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{referralCount}</p>
              <p className="text-xs text-gray-600">Referrals</p>
            </div>
            <div className="text-center bg-white rounded-lg p-3">
              <p className="text-2xl font-bold text-green-600">{freeDaysEarned}</p>
              <p className="text-xs text-gray-600">Days Earned</p>
            </div>
            <div className="text-center bg-white rounded-lg p-3">
              <p className="text-2xl font-bold text-primary-600">{freeDaysAvailable}</p>
              <p className="text-xs text-gray-600">Available</p>
            </div>
          </div>

          {/* Share button */}
          <Button
            variant="primary"
            onClick={handleShareReferral}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
          >
            <ShareIcon className="h-4 w-4 mr-2" />
            Share with Friends
          </Button>
        </Card>
      )}

      {/* Usage Section */}
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <BellIcon className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Usage</h2>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Job Captures This Week</span>
              <span className="font-medium text-gray-900">
                {subscription?.usage?.jobsCapturedThisWeek || 0} / {
                  subscription?.plan === 'pro' ? '∞' : subscription?.limits?.jobCapturesPerWeek
                }
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{
                  width: subscription?.plan === 'pro' ? '0%' : `${Math.min(100, ((subscription?.usage?.jobsCapturedThisWeek || 0) / (subscription?.limits?.jobCapturesPerWeek || 1)) * 100)}%`
                }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Resume Tailoring This Month</span>
              <span className="font-medium text-gray-900">
                {subscription?.usage?.tailorsThisMonth || 0} / {
                  subscription?.plan === 'pro' ? '∞' : subscription?.limits?.tailorsPerMonth
                }
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{
                  width: subscription?.plan === 'pro' ? '0%' : `${Math.min(100, ((subscription?.usage?.tailorsThisMonth || 0) / (subscription?.limits?.tailorsPerMonth || 1)) * 100)}%`
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheckIcon className="h-6 w-6 text-red-400" />
          <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Once you delete your account, all of your data will be permanently removed.
          This action cannot be undone.
        </p>

        <Button
          variant="secondary"
          className="text-red-600 border-red-200 hover:bg-red-50"
          onClick={() => toast.error('Account deletion coming soon')}
        >
          Delete Account
        </Button>
      </Card>
    </div>
  );
};

export default SettingsPage;
